import type { Theme, SxProps } from '@mui/material/styles';
import type { ButtonBaseProps } from '@mui/material/ButtonBase';

import { useState, useEffect, useCallback, useRef } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';

import { Iconify } from 'src/components/iconify';
import { CustomPopover } from 'src/components/custom-popover';
import { useGetClientsQuery, useGetMyClientQuery } from 'src/store/slices/clientsAPISlice';
import type { IClient } from 'src/types/clients';
import { Scrollbar } from 'src/components/scrollbar';
import { appStorage } from 'src/lib/localStorage';
import { CONFIG } from 'src/global-config';
import { TextField, CircularProgress, InputAdornment } from '@mui/material';
import inioLogo from 'src/assets/logo/inio-signature.svg';
import ecollectLogo from 'src/assets/logo/ecollect-signature.svg';
import { useTranslate } from 'src/locales';
import { useCheckFeatureFlag } from 'src/lib/hooks/useCheckFeatureFlag';
import { useHasPermission } from 'src/lib/hooks/useHasPermission';
import { Permission } from 'src/assets/data/permissions';

export type WorkspacesPopoverProps = ButtonBaseProps;

export function WorkspacesPopover({ sx, ...other }: WorkspacesPopoverProps) {
  const { data: client, isLoading } = useGetMyClientQuery();

  return (
    <>
      {isLoading && <CircularProgress size={24} color="primary" />}

      {!isLoading && (
        <Box
          component="div"
          sx={[
            {
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
              gap: { xs: 0.5, ['sm']: 1 },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...other}
        >
          <Avatar alt={client?.name} src={client?.logo_uri || ''} sx={{ width: 24, height: 24 }} />
          <Box
            component="span"
            sx={{ typography: 'subtitle2', display: { xs: 'none', ['sm']: 'inline-flex' } }}
          >
            {client?.name}
          </Box>
        </Box>
      )}
    </>
  );
}

export function WorkspacesPopoverSA({ sx, ...other }: WorkspacesPopoverProps) {
  const { t } = useTranslate();
  const mediaQuery = 'sm';
  const canReadClient = useHasPermission(Permission.ReadClient);
  const { data: clientsResult, isLoading } = useGetClientsQuery(['id', 'name', 'logo_uri'], {
    skip: !canReadClient,
  });
  const [data, setData] = useState<IClient[] | undefined>(clientsResult?.data);
  const [searchValue, setSearchValue] = useState('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (clientsResult?.data) {
      setData(clientsResult?.data);
    }
  }, [clientsResult?.data]);

  const shouldRenderEcollectLogo = useCheckFeatureFlag('bop-should-show-ecollect-logo');

  const { open, anchorEl, onClose, onOpen } = usePopover();
  const [workspace, setWorkspace] = useState<IClient | null>(null);

  const handleChangeWorkspace = useCallback(
    (newValue: any) => {
      setWorkspace(newValue);
      appStorage.setItem(CONFIG.WORKSPACE_LOCAL_STORAGE_KEY, newValue.id);
      onClose();
      window.location.replace('/');
    },
    [onClose]
  );

  const clearWorkspace = useCallback(() => {
    setWorkspace(null);
    appStorage.removeItem(CONFIG.WORKSPACE_LOCAL_STORAGE_KEY);
    onClose();
    window.location.replace('/');
  }, [onClose]);

  useEffect(() => {
    if (data && data?.length && appStorage.getItem(CONFIG.WORKSPACE_LOCAL_STORAGE_KEY)) {
      setWorkspace(
        data.find((x) => x.id == appStorage.getItem(CONFIG.WORKSPACE_LOCAL_STORAGE_KEY)) || data[0]
      );
    }
  }, [data, setWorkspace]);

  const buttonBg: SxProps<Theme> = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: 1,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (theme) =>
      theme.transitions.create(['opacity', 'visibility'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  const renderButton = () => (
    <ButtonBase
      data-test-id="workspace-menu-btn"
      disableRipple
      onClick={onOpen}
      sx={[
        (theme) => ({
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          '&::before': buttonBg,
          borderColor: '#919EAB40',
          borderWidth: 1,
          borderStyle: 'solid',
          borderRadius: 1,
          px: 1,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {isLoading && <CircularProgress size={24} color="primary" />}

      {workspace && (
        <>
          <Avatar
            alt={workspace?.name}
            src={workspace?.logo_uri || ''}
            sx={{ width: 24, height: 24 }}
          />

          <Box
            component="span"
            sx={{ typography: 'subtitle2', display: { xs: 'none', [mediaQuery]: 'inline-flex' } }}
          >
            {`${workspace?.name.slice(0, 30).trim()}${workspace?.name.length > 30 ? '...' : ''}`}
          </Box>

          <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
        </>
      )}

      {!workspace && !isLoading && (
        <>
          {!shouldRenderEcollectLogo ? (
            <Avatar alt="Inio" src={inioLogo} sx={{ width: 24, height: 24 }} />
          ) : (
            <Avatar alt="eCollect" src={ecollectLogo} sx={{ width: 24, height: 24 }} />
          )}

          <Box
            component="span"
            sx={{ typography: 'subtitle2', display: { xs: 'none', [mediaQuery]: 'inline-flex' } }}
          >
            {t('common.allClients')}
          </Box>

          <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
        </>
      )}
    </ButtonBase>
  );

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        arrow: { placement: 'top-left' },
        paper: { sx: { mt: 0.5, ml: -1.55 } },
        transition: { onEntered: () => searchInputRef.current?.focus() },
      }}
    >
      <MenuList sx={{ width: 350 }}>
        <Scrollbar sx={{ maxHeight: 350 }}>
          <TextField
            inputRef={searchInputRef}
            variant="outlined"
            size="small"
            sx={{ width: '99%', mb: 1 }}
            placeholder={t('common.searchForClient')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="lucide:search" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              },
            }}
            value={searchValue}
            onChange={(e) => {
              const searchTerm = e.target.value.toLowerCase();
              setSearchValue(searchTerm);

              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }

              timerRef.current = setTimeout(() => {
                if (searchTerm.length > 0) {
                  setData(
                    clientsResult?.data.filter((client) =>
                      client.name.toLowerCase().includes(searchTerm)
                    )
                  );
                } else {
                  setData(clientsResult?.data);
                }
              }, 500);
            }}
          />

          <MenuItem
            selected={!!workspace}
            onClick={clearWorkspace}
            sx={{ height: 48 }}
            data-test-id="workspace-item-all-clients"
          >
            <Avatar
              alt={!shouldRenderEcollectLogo ? 'Inio' : 'eCollect'}
              src={!shouldRenderEcollectLogo ? inioLogo : ecollectLogo}
              sx={{ width: 24, height: 24 }}
            />

            <Box component="span" sx={{ flexGrow: 1, fontWeight: 'fontWeightMedium' }}>
              {t('common.allClients')}
            </Box>
          </MenuItem>

          {data &&
            data.length > 0 &&
            data?.map((option, index) => (
              <MenuItem
                data-test-id={`workspace-item-${index}`}
                key={option.id}
                selected={option.id === workspace?.id}
                onClick={() => handleChangeWorkspace(option)}
                sx={{ height: 48 }}
              >
                <Avatar
                  alt={option.name}
                  src={option.logo_uri || ''}
                  sx={{ width: 24, height: 24 }}
                />
                <Box component="span" sx={{ flexGrow: 1, fontWeight: 'fontWeightMedium' }}>
                  {option.name.length > 20 ? `${option.name.slice(0, 20)}...` : option.name}
                </Box>
              </MenuItem>
            ))}
        </Scrollbar>
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      {renderButton()}
      {renderMenuList()}
    </>
  );
}
